import { useState, useEffect } from 'react';
import axios from 'axios';
import {API_BASE_URL} from './Constants'
import EnrollmentsList from './EnrollmentsList';
import EnrollmentTable from './EnrollmentTable';

const EnrollmentForm = () => {
  const [formData, setFormData] = useState({
    tutor_id: '',
    student_id: '',
    syllabus: '',
    grade: '',
    subject: '',
    type: '',
    status: 'active'
  });

  const [tutors, setTutors] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState({});

  // Fetch tutors and students data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch tutors (assuming your API endpoint for users with tutor role)
        // const tutorsRes = await axios.get(`${API_BASE_URL}/api/tutors`);
        const tutorsRes = await axios.get(`${API_BASE_URL}/api/products`);
        setTutors(tutorsRes.data.filter(user => user.role === 'tutor'));

        // Fetch students (assuming your API endpoint for users with student role)
        const studentsRes = await axios.get(`${API_BASE_URL}/api/products`);
        setStudents(studentsRes.data.filter(user => user.role === 'student'));
      } catch (err) {
        console.error('Error fetching data', err);
        setMessage('Error fetching data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const validateForm = () => {
    const newErrors = {};

    // Required fields validation
    if (!formData.tutor_id) newErrors.tutor_id = 'Tutor is required';
    if (!formData.student_id) newErrors.student_id = 'Student is required';
    
    // Syllabus validation
    if (!formData.syllabus) newErrors.syllabus = 'Syllabus is required';
    
    // Grade validation
    if (!formData.grade) newErrors.grade = 'Grade is required';
    else if (isNaN(formData.grade) || formData.grade < 1 || formData.grade > 12) {
      newErrors.grade = 'Grade must be a number between 1 and 12';
    }
    
    // Subject validation
    if (!formData.subject) newErrors.subject = 'Subject is required';
    
    // Type validation
    if (!formData.type) newErrors.type = 'Type is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = {...prev};
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form before submission
    if (!validateForm()) {
      setMessage('Please fix the errors in the form');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API_BASE_URL}/api/enrollments`, formData);
      setMessage('Course created successfully!');
      // Reset form
      setFormData({
        tutor_id: '',
        student_id: '',
        syllabus: '',
        grade: '',
        subject: '',
        type: '',
        status: 'active'
      });
      setErrors({});
    } catch (err) {
      console.error('Error creating enrollment', err);
      setMessage('Error creating enrollment');
      
      // Handle API validation errors
      if (err.response && err.response.data && err.response.data.errors) {
        const apiErrors = {};
        err.response.data.errors.forEach(error => {
          apiErrors[error.field] = error.message;
        });
        setErrors(apiErrors);
      }
    } finally {
      setLoading(false);
    }
  };

  const grades = Array.from({ length: 12 }, (_, i) => i + 1);
  const subjects = ['PHYSICS', 'CHEMISTRY', 'BIOLOGY', 'MATHS', 'ENGLISH'];
  const syllabi = ['CBSE', 'ICSE', 'IGCSE', 'KERALA STATE'];
  const types = ['ONETOONE', 'GROUP', 'OTHERS'];
  const statuses = ['active', 'inactive', 'completed'];

  if (loading && !formData) {
    return (
      <div className="flex justify-center items-center h-64">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    );
  }

  return (
    <>
    <div className="max-w-2xl mx-auto p-6">
      <h1 className="text-3xl font-bold text-center mb-8">Create Course</h1>
      
      {message && (
        <div className={`alert ${message.includes('Error') ? 'alert-error' : 'alert-success'} mb-6`}>
          <span>{message}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="card bg-base-200 shadow-xl p-6">
        {/* Tutor Selection */}
        <div className="form-control mb-4">
          <label className="label">
            <span className="label-text font-semibold">Tutor</span>
          </label>
          <select
            name="tutor_id"
            value={formData.tutor_id}
            onChange={handleChange}
            className={`select w-full ${errors.tutor_id ? 'select-error' : 'select-bordered'}`}
            required
          >
            <option value="">Select Tutor</option>
            {tutors.map((tutor) => (
              <option key={tutor.id} value={tutor.id}>
                {tutor.name || `Tutor #${tutor.id}`}
              </option>
            ))}
          </select>
          {errors.tutor_id && (
            <label className="label">
              <span className="label-text-alt text-error">{errors.tutor_id}</span>
            </label>
          )}
        </div>

        {/* Student Selection */}
        <div className="form-control mb-4">
          <label className="label">
            <span className="label-text font-semibold">Student</span>
          </label>
          <select
            name="student_id"
            value={formData.student_id}
            onChange={handleChange}
            className={`select w-full ${errors.student_id ? 'select-error' : 'select-bordered'}`}
            required
          >
            <option value="">Select Student</option>
            {students.map((student) => (
              <option key={student.id} value={student.id}>
                {student.name || `Student #${student.id}`}
              </option>
            ))}
          </select>
          {errors.student_id && (
            <label className="label">
              <span className="label-text-alt text-error">{errors.student_id}</span>
            </label>
          )}
        </div>

        {/* Syllabus Selection */}
        <div className="form-control mb-4">
          <label className="label">
            <span className="label-text font-semibold">Syllabus</span>
          </label>
          <select
            name="syllabus"
            value={formData.syllabus}
            onChange={handleChange}
            className={`select w-full ${errors.syllabus ? 'select-error' : 'select-bordered'}`}
            required
          >
            <option value="">Select Syllabus</option>
            {syllabi.map((syllabus) => (
              <option key={syllabus} value={syllabus}>
                {syllabus}
              </option>
            ))}
          </select>
          {errors.syllabus && (
            <label className="label">
              <span className="label-text-alt text-error">{errors.syllabus}</span>
            </label>
          )}
        </div>

        {/* Grade Selection */}
        <div className="form-control mb-4">
          <label className="label">
            <span className="label-text font-semibold">Grade</span>
          </label>
          <select
            name="grade"
            value={formData.grade}
            onChange={handleChange}
            className={`select w-full ${errors.grade ? 'select-error' : 'select-bordered'}`}
            required
          >
            <option value="">Select Grade</option>
            {grades.map((grade) => (
              <option key={grade} value={grade}>
                Grade {grade}
              </option>
            ))}
          </select>
          {errors.grade && (
            <label className="label">
              <span className="label-text-alt text-error">{errors.grade}</span>
            </label>
          )}
        </div>

        {/* Subject Selection */}
        <div className="form-control mb-4">
          <label className="label">
            <span className="label-text font-semibold">Subject</span>
          </label>
          <select
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            className={`select w-full ${errors.subject ? 'select-error' : 'select-bordered'}`}
            required
          >
            <option value="">Select Subject</option>
            {subjects.map((subject) => (
              <option key={subject} value={subject}>
                {subject}
              </option>
            ))}
          </select>
          {errors.subject && (
            <label className="label">
              <span className="label-text-alt text-error">{errors.subject}</span>
            </label>
          )}
        </div>

        {/* Type Selection */}
        <div className="form-control mb-4">
          <label className="label">
            <span className="label-text font-semibold">Type</span>
          </label>
          <select
            name="type"
            value={formData.type}
            onChange={handleChange}
            className={`select w-full ${errors.type ? 'select-error' : 'select-bordered'}`}
            required
          >
            <option value="">Select Type</option>
            {types.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
          {errors.type && (
            <label className="label">
              <span className="label-text-alt text-error">{errors.type}</span>
            </label>
          )}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          className={`btn btn-primary w-full ${loading ? 'btn-disabled' : ''}`}
          disabled={loading}
        >
          {loading ? (
            <span className="loading loading-spinner"></span>
          ) : (
            'Create Course'
          )}
        </button>
      </form>
    </div>
   {!loading && <EnrollmentTable />}
    </>
  );
};

export default EnrollmentForm;